import { format } from "date-fns";
import { it } from 'date-fns/locale/it';
import { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../lib/redux/hooks';
import { colors } from "../../ui/colors";
import { ArrowLeftIcon } from "../../ui/icons/arrow-left";
import { Layout } from "../../ui/layout";
import { CustomAccordion } from '../../ui/molecules/accordion';
import { ButtonComponent } from "../../ui/molecules/button/newButton";
import { Calendar } from "../../ui/molecules/calendar";
import { MadfarmInput } from "../../ui/molecules/input/newInput";
import RadioButtonGroup from "../../ui/molecules/RadioButtonGroup";
import { MadfarmStaticSelect } from "../../ui/molecules/select/newSelect/static";
import { ErrorPopup } from "../../ui/organisms/errorPopup";
import { MenuItems } from "../../ui/organisms/navbar/dto";
import { RightBar } from "../../ui/organisms/rightBar";
import { SuccessPopup } from "../../ui/organisms/successPopup";
import { OptionType, mapRole, officeOptions, role, sot } from "../../utils";
import { GetByIdAnagraphic, resetGetByIdStatus } from "../gestionePersonale/boxAnagrafica/slice";
import { GetAllPersonalManagement, resetGetAllPersonalManagementResourcesStatus } from "../gestionePersonale/slice";
import { GetHeadQuarter, resetGetAllSot } from "../sot/slice";
import { UserCreation, UserValidation, resetCreationStatusIdle, resetUserValidationStatus, setActivationDate, setEmail, setFiscalCode, setHeadQuarter, setIsMeasuresSignature, setName, setOffice, setOfficeProt, setPMId, setPec, setPhone, setRole, setSotProt, setSurname, setUsername } from "./slice";
import './style.scss';

registerLocale('it', it)

interface PersonalResourceDTO {
    name: string
    surname: string
    fiscalCode: string
    email: string
    pec: string
    phone: string
    pmId: string
}

enum SotProtOptionsCode {
    SI = 'SI',
    NO = 'NO',
}

enum OfficeProtOptionsCode {
    SI = 'SI',
    NO = 'NO',
}

enum OptionYesOrNot {
    SI = 'SI',
    NO = 'NO',
}

export function User() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const usersState = useAppSelector(state => state.users)
    const personalManagementState = useAppSelector(state => state.personalManagement)
    const anagraficaState = useAppSelector(state => state.PMAnagrafica)
    const sotState = useAppSelector(state => state.headQuarter)

    const [roleTable, setRoleTable] = useState('')
    const [roleRegione, setRoleRegione] = useState('')
    const [roleNorma, setRoleNorma] = useState('')
    const [roleSelect, setRoleSelect] = useState<string | undefined>(undefined)
    const [selectedOptionOfficeProt, setSelectedOptionOfficeProt] = useState<OfficeProtOptionsCode | null>(null)
    const [selectedOptionSotProt, setSelectedOptionSotProt] = useState<SotProtOptionsCode | null>(null)
    const [selectedIsMeasureSignature, setSelectedIsMeasureSignature] = useState<OptionYesOrNot | null>(null)
    const [personalResources, setPersonalResources] = useState<PersonalResourceDTO[]>([])
    const [headquarter, setHeadquarters] = useState<OptionType[]>([])
    const [expanded, setExpanded] = useState<Record<string, boolean>>({
        panel1: true,
        panel2: true,
    })

    const handleToggleAccordion = (panel: string) => {
        setExpanded(prev => ({
            ...prev,
            [panel]: !prev[panel],
        }))
    }

    useEffect(() => {
        dispatch(GetAllPersonalManagement({}))
        dispatch(GetHeadQuarter(sotState.sotFilters))
    }, [])

    useEffect(() => {
        if (sotState.responseHeadQuarterStatus === 'successfully' && sotState.responseHeadQuarter) {
            const data = sotState.responseHeadQuarter.data
            const newObjects: OptionType[] = data.map(d => ({ value: d.id, label: `${d.code}` }))
            setHeadquarters(newObjects)
            dispatch(resetGetAllSot())
        }
    }, [sotState.responseHeadQuarterStatus])

    useEffect(() => {
        if (
            personalManagementState.getAllPersonalManagementResourcesStatus === 'successfully' &&
            personalManagementState.getAllPersonalManagementResourcesResponse !== undefined
        ) {
            dispatch(resetGetAllPersonalManagementResourcesStatus())
            personalManagementState.getAllPersonalManagementResourcesResponse.data.forEach(pm => {
                if (pm.anagraphicId !== null) {
                    dispatch(GetByIdAnagraphic(String(pm.anagraphicId)))
                }
            })
        }
    }, [personalManagementState.getAllPersonalManagementResourcesStatus])

    useEffect(() => {
        if (
            anagraficaState.GetByIdAnagraphicStatus === 'successfully' &&
            anagraficaState.getByIdAnagraphicRequest !== undefined
        ) {
            dispatch(resetGetByIdStatus('idle'))
            const data = anagraficaState.getByIdAnagraphicRequest
            const newPR: PersonalResourceDTO = {
                name: data.name,
                surname: data.surname,
                fiscalCode: data.fiscalCode,
                email: data.firstEmail,
                pec: data.pec,
                phone: data.firstPhone,
                pmId: data.personalManagementId
            }

            setPersonalResources(prev => {
                if (prev.some(pr => pr.pmId === newPR.pmId)) {
                    return prev
                }
                return [...prev, newPR]
            })
        }
    }, [anagraficaState.GetByIdAnagraphicStatus])

    useEffect(() => {
        if (!roleSelect) {
            return
        }

        mapRole
            .filter(({ role }) => role === roleSelect)
            .forEach(el => {
                setRoleTable(el.label)
                setRoleRegione(el.regioni)
                setRoleNorma(el.normativa)
            })
    }, [roleSelect])

    useEffect(() => {
        if (usersState.userValidationStatus === 'successfully') {
            dispatch(resetUserValidationStatus())
            dispatch(UserCreation(usersState.usersEditCreateRequest))
        }
    }, [usersState.userValidationStatus])

    const handleOptionOfficeProtChange = (value: OfficeProtOptionsCode) => {
        setSelectedOptionOfficeProt(value)
        dispatch(setOfficeProt(value === 'SI'))
    }

    const handleOptionSotProtChange = (value: SotProtOptionsCode) => {
        setSelectedOptionSotProt(value)
        dispatch(setSotProt(value === 'SI'))
    }

    const handleOptionIsMeasureSignature = (value: OptionYesOrNot) => {
        setSelectedIsMeasureSignature(value)
        dispatch(setIsMeasuresSignature(value === 'SI'))
    }

    return (
        <Layout
            menuItem={MenuItems.USERS}
            breadcrumbItems={['Utenti', 'Nuovo Utente']}
            headerLabel="Nuovo Utente"
            rightBar={true}
            headerIcon={
                <ArrowLeftIcon onClick={() => navigate('/users')} color={colors.brandPrimary[50]} size={0} />
            }
            headerChildren={
                <div className="flex items-center h-full w-full justify-end gap-2">
                    <ButtonComponent
                        size={"sm"}
                        iconPosition={"off"}
                        variant={"solid"}
                        label="Salva"
                        color={"orange"}
                        onClick={() => {
                            dispatch(UserValidation(usersState.usersEditCreateRequest))
                        }}
                    />
                </div>
            }
        >
            <div className="flex flex-col w-full h-full overflow-hidden">
                <div className="bg-white shrink-0">
                    {(usersState.userCreationStatus === 'successfully' || usersState.userCreationStatus === 'failed') && (
                        <div className="px-6 pt-3 pb-1 flex flex-col gap-2">
                            <SuccessPopup
                                active={usersState.userCreationStatus === 'successfully'}
                                message="Dati salvati correttamente."
                                onClose={() => {
                                    dispatch(resetCreationStatusIdle())
                                    navigate('/users')
                                }}
                            />
                            <ErrorPopup
                                active={usersState.userCreationStatus === 'failed'}
                                message="Si è verificato un errore durante la creazione dell'utente"
                                onClose={() => {
                                    dispatch(resetCreationStatusIdle())
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-[minmax(0,7fr)_minmax(0,3fr)] flex-1 overflow-hidden overflow-x-hidden">
                    <div className="min-w-0 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] bg-neutral-50 border-r border-neutral-200 p-6 flex flex-col gap-6">
                        <CustomAccordion
                            id="panel1"
                            title={<span className="text-uppercase text-white label-input">Credenziali</span>}
                            expanded={expanded.panel1}
                            onChange={() => handleToggleAccordion('panel1')}
                            detailsClassName="flex flex-col gap-4"
                        >
                            <div className="grid grid-cols-1 gap-4">
                                <MadfarmInput
                                    label={'Username'}
                                    placeholder="Username"
                                    error={usersState.createUserErrors.username}
                                    supportingText={usersState.createUserErrors.username ? 'Username mancante' : undefined}
                                    value={usersState.usersEditCreateRequest.username}
                                    onChangeText={(text) => dispatch(setUsername(text))}
                                />
                            </div>
                        </CustomAccordion>

                        <CustomAccordion
                            id="panel2"
                            title={<span className="text-uppercase text-white label-input">Anagrafica</span>}
                            expanded={expanded.panel2}
                            onChange={() => handleToggleAccordion('panel2')}
                            detailsClassName="flex flex-col gap-4"
                        >
                            <div className="grid grid-cols-3 gap-4">
                                <MadfarmInput
                                    value={usersState.usersEditCreateRequest.name}
                                    supportingText={usersState.createUserErrors.name ? "Inserire il nome." : undefined}
                                    error={usersState.createUserErrors.name}
                                    label={'Nome'}
                                    placeholder="Nome"
                                    onChangeText={(text) => dispatch(setName(text))}
                                />
                                <MadfarmInput
                                    value={usersState.usersEditCreateRequest.surname}
                                    supportingText={usersState.createUserErrors.surname ? "Inserire il cognome." : undefined}
                                    error={usersState.createUserErrors.surname}
                                    label={'Cognome'}
                                    placeholder="Cognome"
                                    onChangeText={(text) => dispatch(setSurname(text))}
                                />
                                <MadfarmInput
                                    value={usersState.usersEditCreateRequest.fiscalCode}
                                    supportingText={usersState.createUserErrors.fiscalCode ? "Inserire il codice fiscale (16 caratteri)." : undefined}
                                    error={usersState.createUserErrors.fiscalCode}
                                    label={'Codice Fiscale'}
                                    placeholder="Codice Fiscale"
                                    onChangeText={(text) => dispatch(setFiscalCode(text))}
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <MadfarmInput
                                    value={usersState.usersEditCreateRequest.email}
                                    supportingText={usersState.createUserErrors.email ? "Inserire email" : undefined}
                                    error={usersState.createUserErrors.email}
                                    label={'Email'}
                                    placeholder="Email"
                                    onChangeText={(text) => dispatch(setEmail(text))}
                                />
                                <MadfarmInput
                                    value={usersState.usersEditCreateRequest.pec}
                                    label={'PEC'}
                                    placeholder="PEC"
                                    error={usersState.createUserErrors.pec}
                                    supportingText={usersState.createUserErrors.pec ? 'Pec mancante' : undefined}
                                    onChangeText={(text) => dispatch(setPec(text))}
                                />
                                <MadfarmInput
                                    value={usersState.usersEditCreateRequest.phone}
                                    label={'Cellulare'}
                                    placeholder="Cellulare"
                                    error={usersState.createUserErrors.phone}
                                    supportingText={usersState.createUserErrors.phone ? 'Deve essere compreso tra 7 e 15 caratteri' : undefined}
                                    onChangeText={(text) => dispatch(setPhone(text))}
                                />
                            </div>
                        </CustomAccordion>
                    </div>

                    <div className="min-w-0 overflow-y-auto bg-white">
                        <RightBar>
                            <div className="flex flex-col gap-5 w-full">
                                <div className="flex flex-col gap-3">
                                    <span className="text-sm font-semibold uppercase tracking-widest text-brandPrimary-600">Sede<span className="text-red-500"> *</span></span>
                                    <MadfarmStaticSelect
                                        placeholder={'Sede'}
                                        error={usersState.createUserErrors.sot}
                                        errorLabel={'Sede mancante'}
                                        options={headquarter}
                                        value={usersState.usersEditCreateRequest.headQuarterId}
                                        onChange={(value) => {
                                            // @ts-ignore
                                            const newValue = value !== null ? value.value : null
                                            dispatch(setHeadQuarter(newValue))
                                        }}
                                        noOptionsMessage={'Nessuna sede trovata'}
                                    />
                                </div>

                                <div className="border-t border-neutral-200" />
                                <div className="flex flex-col gap-3">
                                    <span className="text-sm font-semibold uppercase tracking-widest text-brandPrimary-600">Ufficio</span>
                                    <MadfarmStaticSelect
                                        placeholder={'Ufficio'}
                                        options={officeOptions}
                                        value={usersState.usersEditCreateRequest.office}
                                        onChange={(value) => {
                                            // @ts-ignore
                                            const newValue = value !== null ? value.value : null
                                            dispatch(setOffice(newValue))
                                        }}
                                        noOptionsMessage={'Nessun ufficio trovato'}
                                    />
                                </div>

                                <div className="border-t border-neutral-200" />
                                <div className="flex flex-col gap-3">
                                    <span className="text-sm font-semibold uppercase tracking-widest text-brandPrimary-600">Personale</span>
                                    <MadfarmStaticSelect
                                        placeholder={'Personale'}
                                        options={personalResources.map(pr => ({ value: pr.pmId, label: `${pr.name} ${pr.surname}` }))}
                                        onChange={(value) => {
                                            // @ts-ignore
                                            const newValue = value !== null ? value.value : null
                                            const selected = personalResources.find(pr => pr.pmId === newValue)
                                            if (selected) {
                                                dispatch(setName(selected.name))
                                                dispatch(setSurname(selected.surname))
                                                dispatch(setEmail(selected.email))
                                                dispatch(setPhone(selected.phone))
                                                dispatch(setFiscalCode(selected.fiscalCode))
                                                dispatch(setPec(selected.pec))
                                            }
                                            dispatch(setPMId(newValue))
                                        }}
                                        noOptionsMessage={'Nessun personale trovato'}
                                    />
                                </div>

                                <div className="border-t border-neutral-200" />
                                <div className="flex flex-col gap-3">
                                    <span className="text-sm font-semibold uppercase tracking-widest text-brandPrimary-600">Ruolo<span className="text-red-500"> *</span></span>
                                    <MadfarmStaticSelect
                                        placeholder={'Ruolo'}
                                        options={role}
                                        error={usersState.createUserErrors.role}
                                        errorLabel={'Ruolo mancante'}
                                        value={usersState.usersEditCreateRequest.role}
                                        onChange={(value) => {
                                            // @ts-ignore
                                            const newValue = value !== null ? value.value : null
                                            dispatch(setRole(newValue))
                                            setRoleTable('')
                                            setRoleRegione('')
                                            setRoleNorma('')
                                            setRoleSelect(newValue)
                                        }}
                                        noOptionsMessage={'Nessun ruolo trovato'}
                                    />
                                </div>

                                <div className="rounded-xl border border-neutral-200 bg-neutral-50">
                                    <div className="grid grid-cols-[120px_1fr] border-b border-neutral-200">
                                        <div className="px-3 py-2 font-semibold text-brandPrimary-700">Ufficio</div>
                                        <div className="px-3 py-2 text-neutral-700">{roleTable}</div>
                                    </div>
                                    <div className="grid grid-cols-[120px_1fr] border-b border-neutral-200">
                                        <div className="px-3 py-2 font-semibold text-brandPrimary-700">Normativa</div>
                                        <div className="px-3 py-2 text-neutral-700">{roleNorma}</div>
                                    </div>
                                    <div className="grid grid-cols-[120px_1fr]">
                                        <div className="px-3 py-2 font-semibold text-brandPrimary-700">Regioni</div>
                                        <div className="px-3 py-2 text-neutral-700">{roleRegione}</div>
                                    </div>
                                </div>

                                <div className="border-t border-neutral-200" />
                                <div className="flex flex-col gap-2 text-left">
                                    <span className="text-sm font-semibold uppercase tracking-widest text-brandPrimary-600">Data Attivazione<span className="text-red-500"> *</span></span>
                                    <Calendar
                                        errorLabel={usersState.createUserErrors.activationDate ? 'Data di attivazione mancante' : undefined}
                                        selected={usersState.usersEditCreateRequest.activationDate}
                                        onChange={(date) => {
                                            dispatch(setActivationDate(format(new Date(date), 'yyyy-MM-dd')))
                                        }}
                                    />
                                </div>

                                <div className="border-t border-neutral-200" />
                                <div className="flex flex-col gap-2 text-left">
                                    <span className="text-sm font-semibold uppercase tracking-widest text-brandPrimary-600">Firma Provvedimento</span>
                                    <RadioButtonGroup
                                        name={'is_measure_signature'}
                                        options={Object.values(OptionYesOrNot)}
                                        selectedValue={selectedIsMeasureSignature}
                                        onChange={handleOptionIsMeasureSignature}
                                        optionLabels={{
                                            [OptionYesOrNot.SI]: 'Si',
                                            [OptionYesOrNot.NO]: 'No',
                                        }}
                                    />
                                </div>

                                {(usersState.usersEditCreateRequest.headQuarterId || usersState.usersEditCreateRequest.office) && (
                                    <>
                                        <div className="border-t border-neutral-200" />
                                        <div className="flex flex-col gap-3">
                                            <span className="text-sm font-semibold uppercase tracking-widest text-brandPrimary-600">Gestione Protocollo</span>

                                            {usersState.usersEditCreateRequest.headQuarterId && (
                                                <div className="flex flex-col gap-2 text-left">
                                                    <span className="text-sm font-semibold text-neutral-700">
                                                        Sede: {
                                                            sot
                                                                .filter(({ value }) => value === usersState.usersEditCreateRequest.headQuarterId)
                                                                .map(el => el.label)
                                                                .join(', ')
                                                        }
                                                    </span>
                                                    <RadioButtonGroup
                                                        name={'prot_sot_able'}
                                                        options={Object.values(SotProtOptionsCode)}
                                                        selectedValue={selectedOptionSotProt}
                                                        onChange={handleOptionSotProtChange}
                                                        optionLabels={{
                                                            [SotProtOptionsCode.SI]: 'Si',
                                                            [SotProtOptionsCode.NO]: 'No',
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {usersState.usersEditCreateRequest.office && (
                                                <div className="flex flex-col gap-2 text-left">
                                                    <span className="text-sm font-semibold text-neutral-700">
                                                        Ufficio: {
                                                            officeOptions
                                                                .filter(({ value }) => value === usersState.usersEditCreateRequest.office)
                                                                .map(el => el.label)
                                                                .join(', ')
                                                        }
                                                    </span>
                                                    <RadioButtonGroup
                                                        name={'prot_office_able'}
                                                        options={Object.values(OfficeProtOptionsCode)}
                                                        selectedValue={selectedOptionOfficeProt}
                                                        onChange={handleOptionOfficeProtChange}
                                                        optionLabels={{
                                                            [OfficeProtOptionsCode.SI]: 'Si',
                                                            [OfficeProtOptionsCode.NO]: 'No',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </RightBar>
                    </div>
                </div>
            </div>
        </Layout>
    )
}
